import { Currency, Form, SectionItem } from '@mairistem/composants';
import { isNewIdentifiant, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { printRelance } from '@/routes';
import type { ElementDisponibleType, ErrorFieldType } from '@/types';
import ModalMailRelance from './ModalMailRelance';
import ModalTexteRelance from './ModalTexteRelance';

// Noms des champs émis via onChange (et utilisés pour le matching des erreurs).
// Permet de réutiliser ce composant pour les relances « avant » et « après » date limite,
// chaque instance ciblant son propre jeu de colonnes.
type RelanceFieldNames = {
  seuil: string;
  envoi: string;
  texte: string;
  html: string;
};

// Noms des champs des relances « après date limite » (comportement historique).
const DEFAULT_FIELD_NAMES: RelanceFieldNames = {
  seuil: 'seuilRelance',
  envoi: 'envoiRelanceParMail',
  texte: 'texteRelance',
  html: 'htmlMailRelance',
};

type RelanceProps = {
  identifiant?: string | number;
  errors?: ErrorFieldType[];
  title?: string;
  /** Enveloppe le contenu dans une SectionItem titrée. Mettre à false pour laisser le parent gérer l'entête. */
  withSection?: boolean;
  /** Désactive l'ensemble de la section (boutons doc/aperçu/mail + coche envoi). */
  disabled?: boolean;
  /** Noms des champs remontés au modèle régie. */
  names?: RelanceFieldNames;
  /** Builder d'URL pour l'aperçu PDF. */
  printRoute?: (id: number, job?: boolean, mail?: boolean) => string;
  /** Préfixe des data-testid (ex. "regie-prolongee" ou "relance-avant-echeance"). */
  testIdPrefix?: string;
  /** Éléments insérables de la modale de personnalisation du document. Défaut : relance après-échéance. */
  elements?: ElementDisponibleType[];
  /** Texte par défaut de la modale de personnalisation du document. Défaut : relance après-échéance. */
  texteDefaut?: string;
  /** Éléments insérables de la modale de personnalisation du mail. Défaut : relance après-échéance. */
  mailElements?: ElementDisponibleType[];
  /** Contenu HTML par défaut de la modale de personnalisation du mail. Défaut : relance après-échéance. */
  htmlMailDefaut?: string;
  envoiRelanceParMail?: boolean;
  seuilRelance?: number | string;
  texteRelance?: string;
  htmlMailRelance?: string;
  updated?: boolean;
  onChange?: (data: any) => void;
};

const Relance = (props: RelanceProps) => {
  const {
    identifiant,
    errors,
    title = 'Relances',
    withSection = true,
    disabled = false,
    names = DEFAULT_FIELD_NAMES,
    printRoute = printRelance,
    testIdPrefix = 'regie-prolongee',
    elements,
    texteDefaut,
    mailElements,
    htmlMailDefaut,
    envoiRelanceParMail = false,
    seuilRelance,
    texteRelance,
    htmlMailRelance,
    updated,
    onChange,
  } = props;

  const [modalTexteRelanceOpen, setModalTexteRelanceOpen] = React.useState<boolean>(false);
  const [modalMailRelanceOpen, setModalMailRelanceOpen] = React.useState<boolean>(false);
  const [loadingPreview, setLoadingPreview] = React.useState<boolean>(false);

  const handleCurrencyChange = React.useCallback(
    (_event, value) => {
      if (onChange) {
        onChange({
          identifiant,
          [names.seuil]: value,
        });
      }
    },
    [identifiant, onChange, names.seuil],
  );

  const handleCheckboxChange = React.useCallback(
    (e, { checked }) => {
      if (onChange) {
        onChange({
          identifiant,
          [names.envoi]: checked,
        });
      }
    },
    [identifiant, onChange, names.envoi],
  );

  const handleModalTexteRelanceToggle = React.useCallback(() => {
    setModalTexteRelanceOpen((p) => !p);
  }, []);

  const handleModalMailRelanceToggle = React.useCallback(() => {
    setModalMailRelanceOpen((p) => !p);
  }, []);

  const handleTexteRelanceSave = React.useCallback(
    (texte: string) => {
      if (onChange) {
        onChange({
          identifiant,
          [names.texte]: texte,
        });
      }

      setModalTexteRelanceOpen(false);
    },
    [identifiant, onChange, names.texte],
  );

  const handleMailRelanceSave = React.useCallback(
    (html: string) => {
      if (onChange) {
        onChange({
          identifiant,
          [names.html]: html,
        });
      }

      setModalMailRelanceOpen(false);
    },
    [identifiant, onChange, names.html],
  );

  const handlePreviewPrint = React.useCallback(() => {
    setLoadingPreview(true);

    request
      .get(printRoute(0), { headers: { Accept: 'application/json, application/pdf' } })
      .then((edition) => {
        const { url: urlS3, data }: { url?: string; data?: any } = edition || {};

        // Si la réponse contient une URL temporaire (JSON)
        if (urlS3) {
          window.open(urlS3, '_blank');
        } else {
          // Sinon, traiter comme un blob PDF (réponse directe)
          const blob = data || edition;
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          const url = URL.createObjectURL(pdfBlob);

          window.open(url, '_blank');

          // Nettoyer après un délai
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 2000);
        }
      })
      .finally(() => {
        setLoadingPreview(false);
      });
  }, [printRoute]);

  const content = (
    <>
      <Form.Group
        style={{
          display: 'flex',
          alignItems: 'flex-start',
        }}
        inline
      >
        <Form.Field
          // @ts-expect-error
          id={names.seuil}
          width={8}
          error={_.some(errors, { id: names.seuil })}
        >
          <label>Seuil de relance</label>
          <Currency
            data-testid={`${testIdPrefix}-input-seuil-relance`}
            value={seuilRelance}
            name={names.seuil}
            placeholder=""
            // Description vide obligatoire pour que l'input passe en erreur...
            description=""
            style={{ width: 100 }}
            valueMin={0}
            onBlur={handleCurrencyChange}
            devise
          />
        </Form.Field>
        <Form.Button
          data-testid={`${testIdPrefix}-button-parametrage-lettre-relance`}
          basic
          disabled={disabled}
          content="Personnalisation du document"
          icon="setting"
          onClick={handleModalTexteRelanceToggle}
        />
        <Form.Button
          data-testid={`${testIdPrefix}-button-aperçu-lettre-relance`}
          basic
          disabled={disabled || isNewIdentifiant(identifiant) || updated}
          icon="eye"
          title="Aperçu d'une relance"
          loading={loadingPreview}
          onClick={handlePreviewPrint}
        />
      </Form.Group>
      <Form.Group
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
        inline
      >
        <Form.Checkbox
          data-testid={`${testIdPrefix}-checkbox-relance-par-mail`}
          toggle
          label="Envoi par mail"
          checked={envoiRelanceParMail}
          disabled={disabled}
          onChange={handleCheckboxChange}
          name={names.envoi}
          width={8}
        />
        <Form.Button
          data-testid={`${testIdPrefix}-button-parametrage-mail-relance`}
          basic
          disabled={disabled || !envoiRelanceParMail}
          floated="right"
          content="Personnalisation du mail"
          icon="setting"
          onClick={handleModalMailRelanceToggle}
        />
      </Form.Group>

      {modalTexteRelanceOpen && (
        <ModalTexteRelance
          texteRelance={texteRelance}
          elements={elements}
          texteDefaut={texteDefaut}
          onClose={handleModalTexteRelanceToggle}
          onSave={handleTexteRelanceSave}
        />
      )}

      {modalMailRelanceOpen && (
        <ModalMailRelance
          htmlMailRelance={htmlMailRelance}
          elements={mailElements}
          htmlDefaut={htmlMailDefaut}
          onClose={handleModalMailRelanceToggle}
          onSave={handleMailRelanceSave}
        />
      )}
    </>
  );

  if (!withSection) {
    return content;
  }

  return (
    <SectionItem title={title} single compact>
      {content}
    </SectionItem>
  );
};

export default Relance;
